import { useEffect, useRef, useState } from 'react';
import { useJoyrideStore } from '@/stores/joyride/joyrideStore';
import { scrollTargetIntoView } from '@/utils/joyrideScrolling';
import useJoyrideExperiment from '@/hooks/useJoyrideExperiment';

/**
 * Reusable hook for registering a Joyride tour on a page.
 *
 * Handles:
 * - Starting the tour when `enabled` becomes true
 * - Stopping the tour when navigating away (unmount)
 * - Always starts from the beginning (no pause/resume)
 *
 * @param {Object} options
 * @param {string} options.key - Unique tour key (e.g. 'personality-form')
 * @param {Array} options.steps - Joyride step definitions
 * @param {string} [options.chatbotId] - Chatbot ID for per-chatbot completion tracking
 * @param {string} [options.scrollContainerSelector] - CSS selector for the scroll container
 * @param {boolean} [options.enabled=true] - Whether the tour should start (reactive)
 * @param {number} [options.delay=0] - Delay in ms before starting the tour
 * @param {boolean} [options.disableAutoScroll=false] - Skip auto-scrolling to the first step
 * @returns {{ isActive: boolean }}
 */
const useJoyrideTour = ({ key, steps, chatbotId, scrollContainerSelector, enabled = true, delay = 0, disableAutoScroll = false }) => {
  const startTour = useJoyrideStore((state) => state.startTour);
  const stopTour = useJoyrideStore((state) => state.stopTour);
  const activeTourKey = useJoyrideStore((state) => state.activeTourKey);
  const run = useJoyrideStore((state) => state.run);
  const { joyrideEnabled } = useJoyrideExperiment();

  const keyRef = useRef(key);
  keyRef.current = key;
  const hasStartedRef = useRef(false);
  const [delayPassed, setDelayPassed] = useState(delay === 0);

  // Handle delay
  useEffect(() => {
    if (!enabled || delay === 0) return;
    setDelayPassed(false);
    const timer = setTimeout(() => setDelayPassed(true), delay);
    return () => clearTimeout(timer);
  }, [enabled, delay]);

  useEffect(() => {
    if (!enabled || !delayPassed || !joyrideEnabled) return;
    if (hasStartedRef.current) return;

    hasStartedRef.current = true;
    startTour({
      key,
      steps,
      chatbotId,
      scrollContainerSelector,
    });

    // Scroll to the first step's target
    if (!disableAutoScroll) {
      const firstStep = steps?.[0];
      if (firstStep?.target) {
        setTimeout(() => {
          scrollTargetIntoView(firstStep.target, scrollContainerSelector);
        }, 300);
      }
    }
  }, [key, chatbotId, enabled, delayPassed, joyrideEnabled]);

  // Stop tour on unmount if this tour is active
  useEffect(() => {
    return () => {
      const state = useJoyrideStore.getState();
      if (state.activeTourKey === keyRef.current && state.run) {
        stopTour();
      }
    };
  }, []);

  const isActive = activeTourKey === key && run;

  return { isActive };
};

export default useJoyrideTour;
